import { useEffect, useMemo, useState } from "react";
import { useDogs } from "@/context/DogsContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { SectionTitle } from "@/components/app/Bits";
import BrandHero from "@/components/app/BrandHero";
import EmptyDogState from "@/components/app/EmptyDogState";
import { getPromptsConfig } from "@/lib/dogTypes";
import { Sparkles, CalendarCheck } from "lucide-react";
import { format, startOfWeek, startOfMonth, endOfMonth, addDays } from "date-fns";

export default function GetRemindersPage() {
  const { activeDog } = useDogs();
  const promptsConfig = getPromptsConfig(activeDog?.dog_type);
  const DAILY = promptsConfig.daily;
  const WEEKLY = promptsConfig.weekly;
  const MONTHLY = promptsConfig.monthly;
  const [prompts, setPrompts] = useState([]);
  const today = useMemo(() => new Date(), []);
  const weekStart = useMemo(() => startOfWeek(today, { weekStartsOn: 1 }), [today]);
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const monthStart = useMemo(() => startOfMonth(today), [today]);
  const monthEnd = useMemo(() => endOfMonth(today), [today]);
  const todayStr = format(today, "yyyy-MM-dd");
  const monthStartStr = format(monthStart, "yyyy-MM-dd");
  const monthEndStr = format(monthEnd, "yyyy-MM-dd");

  useEffect(() => {
    if (!activeDog) return;
    (async () => {
      // Fetch the entire month so we cover daily, weekly, and monthly prompts in one call.
      const p = await api.get("/prompts", {
        params: { dog_id: activeDog.id, start: monthStartStr, end: monthEndStr },
      });
      setPrompts(p.data.items || []);
    })();
  }, [activeDog, monthStartStr, monthEndStr]);

  const isCompleted = (key, dateStr) =>
    prompts.find((x) => x.prompt_key === key && x.entry_date === dateStr && x.completed);

  const togglePrompt = async (key, dateStr) => {
    const completed = !isCompleted(key, dateStr);
    await api.post("/prompts/toggle", {
      dog_id: activeDog.id, prompt_key: key, entry_date: dateStr, completed,
    });
    setPrompts((cur) => {
      const filtered = cur.filter((x) => !(x.prompt_key === key && x.entry_date === dateStr));
      return [...filtered, { prompt_key: key, entry_date: dateStr, completed }];
    });
  };

  return (
    <EmptyDogState>
      {activeDog && (
        <div className="space-y-10" data-testid="get-reminders-page">
          <BrandHero
            overline="Get reminders"
            title="Daily, weekly & monthly prompts"
            description="Tick what you did today. We recommend 3–5 days per week of weekly activities for best results."
            testid="get-reminders-hero"
          />

          {/* Today's prompts */}
          <div>
            <SectionTitle overline="Today" title="Daily prompt" />
            <Card className="border-border shadow-none rounded-lg">
              <CardContent className="p-6 space-y-3">
                {DAILY.map((p) => (
                  <label
                    key={p.key}
                    className="flex items-center gap-3 cursor-pointer hover:bg-muted px-3 py-2 rounded-md transition-colors"
                    data-testid={`daily-prompt-${p.key}`}
                  >
                    <Checkbox
                      checked={!!isCompleted(p.key, todayStr)}
                      onCheckedChange={() => togglePrompt(p.key, todayStr)}
                    />
                    <span className={`text-sm ${isCompleted(p.key, todayStr) ? "line-through text-muted-foreground" : ""}`}>
                      {p.label} — {format(today, "EEE, MMM d")}
                    </span>
                  </label>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Weekly */}
          <div>
            <SectionTitle overline={`Week of ${format(weekStart, "MMM d")}`} title="Weekly prompts" />

            <Card className="border-border shadow-none rounded-lg bg-secondary mb-4" data-testid="weekly-recommendation">
              <CardContent className="p-5 flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                  <Sparkles size={18} />
                </div>
                <div className="text-sm">
                  <p className="font-medium">Pick the rhythm that fits your week</p>
                  <p className="text-muted-foreground mt-0.5">
                    Tap a single day to do an activity <strong>once a week</strong>, or tap multiple days for a more
                    consistent routine. We recommend <strong>3&ndash;5 days per week</strong> for best results.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {WEEKLY.map((p) => {
                const doneThisWeek = Array.from({ length: 7 }).reduce((acc, _, i) => {
                  const ds = format(addDays(weekStart, i), "yyyy-MM-dd");
                  return acc + (isCompleted(p.key, ds) ? 1 : 0);
                }, 0);
                const inSweetSpot = doneThisWeek >= 3 && doneThisWeek <= 5;
                return (
                  <Card key={p.key} className="border-border shadow-none rounded-lg" data-testid={`weekly-card-${p.key}`}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <p className="font-medium">{p.label}</p>
                        <span
                          className={`text-[11px] uppercase tracking-[0.18em] px-2 py-0.5 rounded ${
                            inSweetSpot
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground"
                          }`}
                          data-testid={`weekly-count-${p.key}`}
                        >
                          {doneThisWeek} / 7 this week
                        </span>
                      </div>
                      <div className="grid grid-cols-7 gap-1.5">
                        {Array.from({ length: 7 }).map((_, i) => {
                          const d = addDays(weekStart, i);
                          const ds = format(d, "yyyy-MM-dd");
                          const done = !!isCompleted(p.key, ds);
                          return (
                            <button
                              key={ds}
                              onClick={() => togglePrompt(p.key, ds)}
                              data-testid={`weekly-${p.key}-${ds}`}
                              className={`aspect-square rounded-md text-xs flex flex-col items-center justify-center border transition-all ${
                                done
                                  ? "bg-primary text-primary-foreground border-primary"
                                  : "bg-card border-border text-muted-foreground hover:bg-muted"
                              }`}
                            >
                              <span className="text-[10px] uppercase">{format(d, "EEE")[0]}</span>
                              <span className="font-medium">{format(d, "d")}</span>
                            </button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Monthly */}
          {MONTHLY.length > 0 && (
            <div>
              <SectionTitle overline={format(today, "MMMM yyyy")} title="Monthly prompts" />
              <Card className="border-border shadow-none rounded-lg">
                <CardContent className="p-6 space-y-3">
                  {MONTHLY.map((p) => {
                    const ds = monthStartStr; // first-of-month sentinel
                    const done = !!isCompleted(p.key, ds);
                    return (
                      <label
                        key={p.key}
                        className="flex items-center gap-3 cursor-pointer hover:bg-muted px-3 py-2 rounded-md transition-colors"
                        data-testid={`monthly-prompt-${p.key}`}
                      >
                        <Checkbox
                          checked={done}
                          onCheckedChange={() => togglePrompt(p.key, ds)}
                        />
                        <span className={`text-sm flex items-center gap-2 ${done ? "line-through text-muted-foreground" : ""}`}>
                          <CalendarCheck size={14} className={done ? "text-muted-foreground" : "text-primary"} />
                          {p.label} &mdash; {format(today, "MMMM yyyy")}
                        </span>
                      </label>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </EmptyDogState>
  );
}
